import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { CheckCircle2, ArrowRight } from "lucide-react";

const TIERS = [
  {
    name: "Starter",
    price: "$299",
    period: "/mo per warehouse",
    tagline: "For 3PLs getting their operation under control.",
    features: [
      "1 warehouse",
      "Up to 5 team members",
      "Orders, Inbound, Inventory & Returns",
      "Client read-only portals",
      "CSV exports",
      "Email support",
    ],
    highlight: false,
  },
  {
    name: "Growth",
    price: "$599",
    period: "/mo per warehouse",
    tagline: "For scaling 3PLs with VA teams and multiple clients.",
    features: [
      "Up to 3 warehouses",
      "Unlimited team members",
      "Everything in Starter",
      "VA agency workflows & store scoping",
      "Invoicing summaries",
      "Full activity log & notifications",
      "Priority support",
    ],
    highlight: true,
  },
  {
    name: "Enterprise",
    price: "Custom",
    period: "",
    tagline: "For multi-site operators with custom requirements.",
    features: [
      "Unlimited warehouses",
      "Everything in Growth",
      "Onboarding & data migration",
      "Custom roles & SLAs",
      "Dedicated account manager",
    ],
    highlight: false,
  },
];

export default function PricingPage() {
  return (
    <div data-testid="pricing-page">
      <section className="relative pt-36 md:pt-44 pb-16 lp-grid-lines">
        <div className="lp-orb w-[500px] h-[400px] bg-indigo-300/40 top-10 -right-40" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-10 text-center">
          <p className="text-xs md:text-sm uppercase tracking-[0.3em] text-blue-600 font-bold mb-4">Pricing</p>
          <h1 className="lp-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-[#0b1530] leading-[1.05]">
            Simple pricing that <span className="lp-gradient-text">scales with you.</span>
          </h1>
          <p className="mt-7 text-lg md:text-xl text-slate-600 leading-relaxed max-w-2xl mx-auto">
            Per-warehouse plans. No per-order fees, no surprises.
          </p>
        </div>
      </section>

      <section className="pb-24 lg:pb-32">
        <div className="max-w-6xl mx-auto px-5 lg:px-10 grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch">
          {TIERS.map((t, i) => (
            <motion.div
              key={t.name}
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.55, delay: i * 0.1 }}
              className={`relative rounded-2xl p-8 lg:p-9 flex flex-col ${
                t.highlight
                  ? "bg-[#0b1530] text-white shadow-2xl shadow-blue-900/30 border border-blue-500/30 md:-mt-4 md:mb-[-1rem]"
                  : "lp-card border border-slate-200"
              }`}
              data-testid={`pricing-tier-${t.name.toLowerCase()}`}
            >
              {t.highlight && (
                <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 rounded-full bg-gradient-to-r from-sky-400 to-indigo-500 text-white text-xs font-bold uppercase tracking-[0.15em] px-4 py-1.5 shadow-lg">
                  Most Popular
                </span>
              )}
              <h2 className={`lp-display text-xl font-semibold ${t.highlight ? "text-white" : "text-[#0b1530]"}`}>{t.name}</h2>
              <p className={`mt-2 text-sm leading-relaxed ${t.highlight ? "text-slate-300" : "text-slate-500"}`}>{t.tagline}</p>
              <p className="mt-6">
                <span className={`lp-display text-4xl lg:text-5xl font-semibold ${t.highlight ? "text-white" : "text-[#0b1530]"}`}>{t.price}</span>
                <span className={`text-sm ml-1 ${t.highlight ? "text-slate-400" : "text-slate-500"}`}>{t.period}</span>
              </p>
              <ul className="mt-7 space-y-3 flex-1">
                {t.features.map((f) => (
                  <li key={f} className={`flex items-start gap-3 text-[15px] ${t.highlight ? "text-slate-200" : "text-slate-700"}`}>
                    <CheckCircle2 size={18} strokeWidth={2} className={`mt-0.5 shrink-0 ${t.highlight ? "text-sky-400" : "text-blue-600"}`} />
                    {f}
                  </li>
                ))}
              </ul>
              <Link
                to="/contact"
                className={`mt-8 inline-flex justify-center items-center gap-2 rounded-full font-semibold px-6 py-3.5 transition-colors ${
                  t.highlight
                    ? "bg-blue-500 hover:bg-blue-400 text-white shadow-lg shadow-blue-500/40"
                    : "border-2 border-slate-300 hover:border-blue-500 text-slate-700 hover:text-blue-600"
                }`}
                data-testid={`pricing-cta-${t.name.toLowerCase()}`}
              >
                {t.price === "Custom" ? "Talk to Us" : "Get Started"}
                <ArrowRight size={16} strokeWidth={2} />
              </Link>
            </motion.div>
          ))}
        </div>
        <p className="mt-12 text-center text-sm text-slate-500 px-5">
          All plans include strict multi-tenant isolation, role-based access control and audit logging.
        </p>
      </section>
    </div>
  );
}
